import { useRef } from "react";
import { Star, Plus, X, CalendarX } from "lucide-react";

function newFeatureKey() {
  return `f-${Math.random().toString(36).slice(2)}`;
}

function FeaturesEditor({ pkg, keys, onUpdate, onRemove, onAdd }) {
  return (
    <>
      <div className="mb-2 text-[10px] tracking-overline uppercase text-[#8A8782] font-medium">
        Features
      </div>
      <div className="space-y-2">
        {pkg.features.map((f, i) => (
          <div key={keys[i]} className="flex items-center gap-2 group">
            <span className="text-[#A9927D]">·</span>
            <input
              type="text"
              value={f}
              onChange={(e) => onUpdate(i, e.target.value)}
              data-testid={`pkg-feature-${pkg.id}-${i}`}
              className="flex-1 bg-transparent border-b border-transparent hover:border-[#E5E3DB] focus:border-[#2F2E2E] outline-none py-1 text-sm"
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="opacity-0 group-hover:opacity-100 text-[#8A3D3D] transition-opacity"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={onAdd}
          data-testid={`pkg-add-feature-${pkg.id}`}
          className="inline-flex items-center gap-1.5 text-[11px] tracking-overline uppercase text-[#5C5A56] hover:text-[#2F2E2E] pt-2"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add feature
        </button>
      </div>
    </>
  );
}

export function PackageEditor({ pkg, onChange }) {
  const selected = pkg.selected;
  const keysRef = useRef(pkg.features.map(() => newFeatureKey()));
  while (keysRef.current.length < pkg.features.length) keysRef.current.push(newFeatureKey());
  if (keysRef.current.length > pkg.features.length) {
    keysRef.current.length = pkg.features.length;
  }

  function update(patch) {
    onChange({ ...pkg, ...patch });
  }
  function updateFeature(i, value) {
    const next = [...pkg.features];
    next[i] = value;
    update({ features: next });
  }
  function removeFeature(i) {
    keysRef.current.splice(i, 1);
    update({ features: pkg.features.filter((_, idx) => idx !== i) });
  }
  function addFeature() {
    keysRef.current.push(newFeatureKey());
    update({ features: [...pkg.features, ""] });
  }

  return (
    <div
      data-testid={`pkg-editor-${pkg.id}`}
      className={`border p-6 transition-all ${
        selected
          ? "border-[#2F2E2E] bg-white"
          : "border-[#E5E3DB] bg-[#F3F1ED]/40 opacity-70"
      }`}
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <label className="flex items-center gap-3 cursor-pointer select-none flex-1">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => update({ selected: e.target.checked })}
            data-testid={`pkg-selected-${pkg.id}`}
            className="w-4 h-4 accent-[#2F2E2E]"
          />
          <input
            type="text"
            value={pkg.name}
            onChange={(e) => update({ name: e.target.value })}
            data-testid={`pkg-name-${pkg.id}`}
            className="font-display text-2xl bg-transparent border-b border-transparent hover:border-[#D1CEC2] focus:border-[#2F2E2E] outline-none flex-1"
          />
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none text-[10px] tracking-overline uppercase font-medium text-[#5C5A56] mt-2">
          <input
            type="checkbox"
            checked={pkg.recommended}
            onChange={(e) => update({ recommended: e.target.checked })}
            data-testid={`pkg-recommended-${pkg.id}`}
            className="accent-[#A9927D]"
          />
          <Star
            className={`w-3.5 h-3.5 ${pkg.recommended ? "fill-[#A9927D] text-[#A9927D]" : ""}`}
            strokeWidth={1.5}
          />
          Recommended
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none text-[10px] tracking-overline uppercase font-medium text-[#5C5A56] mt-2">
          <input
            type="checkbox"
            checked={!!pkg.unavailable}
            onChange={(e) => update({ unavailable: e.target.checked })}
            data-testid={`pkg-unavailable-${pkg.id}`}
            className="accent-[#8A3D3D]"
          />
          <CalendarX
            className={`w-3.5 h-3.5 ${pkg.unavailable ? "text-[#8A3D3D]" : ""}`}
            strokeWidth={1.5}
          />
          <span className={pkg.unavailable ? "text-[#8A3D3D]" : ""}>Fully booked</span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-6 mb-5">
        <div>
          <label className="block text-[10px] tracking-overline uppercase text-[#8A8782] mb-1">
            Price (£)
          </label>
          <input
            type="number"
            step="0.01"
            value={pkg.price ?? ""}
            onChange={(e) => update({ price: parseFloat(e.target.value) || 0 })}
            data-testid={`pkg-price-${pkg.id}`}
            className="w-full border-b border-[#D1CEC2] bg-transparent py-2 tabular-nums focus:border-[#2F2E2E] outline-none"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-overline uppercase text-[#8A8782] mb-1">
            Original Price (£) · optional for "was" strikethrough
          </label>
          <input
            type="number"
            step="0.01"
            value={pkg.original_price ?? ""}
            onChange={(e) =>
              update({
                original_price: e.target.value
                  ? parseFloat(e.target.value)
                  : null,
              })
            }
            data-testid={`pkg-original-price-${pkg.id}`}
            className="w-full border-b border-[#D1CEC2] bg-transparent py-2 tabular-nums focus:border-[#2F2E2E] outline-none"
          />
        </div>
      </div>

      <FeaturesEditor
        pkg={pkg}
        keys={keysRef.current}
        onUpdate={updateFeature}
        onRemove={removeFeature}
        onAdd={addFeature}
      />
    </div>
  );
}
